// lib/services/capture/capture_service.ts

import ColorCameraPool from "./color_camera_pool";
import CameraInfo, { CameraFacing } from "./camerainfo";
import {
  build_diagnostics,
  format_settings,
  format_capabilities,
  format_constraints,
  extract_zoom,
  selected_summary,
  has_meaningful_settings,
  filter_back_candidates,
  type BackFilterResult,
  WEBRTC_LENS_NOTE,
} from "./camera_diagnostics";
import { friendly_name_for, friendly_with_suffix, format_device_id_suffix } from "./camera_options";
import { build_camera_dump } from "./camera_dump";
import {
  collect_basic_device_info,
  collect_high_entropy,
  derive_model,
  format_device_info_lines,
  type NavigatorLike,
} from "./device_info";
import {
  buildCaptureFrameDiagnosticLog,
  extractCaptureColorSpace,
  extractCaptureFrameRect,
  type CaptureFrameColorSpace,
  type CaptureFrameDiagnostic,
  type CaptureFramePlane,
  type CaptureTexturePlane,
} from "./capture_pipeline_diagnostics";
import { YuvFormat, YuvUploadDescriptor } from "../compute/yuvdefs";
import YuvManager from "../compute/yuvmanager";
import {
  resolve_yuv_format,
  PlaneLayoutLike,
  type FormatSource,
} from "../compute/yuv_format";
import Stage from "../stage/stage";
import { rb_log, rb_debug_log, rb_throttle } from "../compute/rb_log";

// At the top of capture_service.ts
const PREFERRED_VIDEO_WIDTH  = 1280;
const PREFERRED_VIDEO_HEIGHT = 720;
const MIN_VIDEO_WIDTH        = 1280;
const MIN_VIDEO_HEIGHT       = 720;
const MAX_VIDEO_WIDTH        = 1280;
const MAX_VIDEO_HEIGHT       = 720;

/**
 * Owns the ColorCameraPool and drives one frame upload per camera frame.
 *
 * Two execution modes:
 *   - "worker": iOS Safari path. A Worker reads VideoFrames from a
 *     MediaStreamTrackProcessor, calls frame.copyTo(buffer), and posts
 *     the resulting bytes + plane layout back to the main thread, which
 *     does the GPU write. (YUV plane access from VideoFrame is only
 *     allowed on a worker in Safari.)
 *   - "main": Chromium/Android path. Same logic runs on the main thread
 *     using the standard MediaStreamTrackProcessor API.
 *
 * The service exposes the pool publicly so consumers (StickerCompute,
 * video render pipeline) can lease or peek slots as needed.
 *
 * Initialization order:
 *   1. new CaptureService()        -- cheap, no GPU, no permissions.
 *   2. await init()                -- platform detect + camera enumeration.
 *   3. (optional) set_selected()   -- UI overrides default camera choice.
 *   4. await start()               -- user-gesture handler triggers
 *                                     getUserMedia and begins frame pumping.
 *   5. stop()                      -- stops capture but leaves the service
 *                                     reusable for another start().
 *   6. shutdown()                  -- destroys pool, fully tears down.
 */
export default class CaptureService {
  // Pool of GPU camera-frame textures.
  camera_pool: ColorCameraPool;

  // The "active" camera info — driven into the pool / capture stream.
  camera_info: CameraInfo;

  // Enumeration results.
  cameralist: CameraInfo[] = [];
  selected: CameraInfo | null = null;

  // Discovery outcome, so the camera selector can distinguish "no cameras"
  // from "permission needed" / "API unavailable" without duplicating the flow.
  enumerate_state: "idle" | "ok" | "no-permission" | "unavailable" | "error" = "idle";

  // Device-model diagnostics, collected once during discovery (see
  // log_device_info). Surfaced to the camera selector; may legitimately be null
  // when the browser does not expose the exact model.
  device_model: string | null = null;
  device_model_note: string | null = null;

  // Explicit user override (exact deviceId) that takes precedence over the
  // natural selection algorithm. null means "Automatic" (unchanged behavior).
  private explicit_override_id: string | null = null;

  // The source + plain-language reason of the MOST RECENT resolved selection,
  // captured by select_default_camera so the scan-start line can report the
  // camera actually used (including a fallback from an unavailable override to
  // Automatic), not merely what was requested. Logging only.
  private last_selection_source: "manual override" | "Automatic" = "Automatic";
  private last_selection_reason = "no camera selected yet";

  // Raw, un-whitelisted discovery data retained per deviceId so an explicit
  // camera selection can emit a complete diagnostic dump WITHOUT opening a new
  // stream. Populated only from data already captured during the existing
  // enumerate/probe flow: the MediaDeviceInfo reference, its groupId, and the
  // probe track's getSettings/getCapabilities/getConstraints snapshots.
  private raw_by_device_id = new Map<
    string,
    {
      media_device_info: unknown;
      group_id: string | null;
      settings: unknown;
      capabilities: unknown;
      constraints: unknown;
    }
  >();

  // De-dupe guard so concurrent discovery triggers (e.g. the selector's "Find
  // cameras" button and the scan boot path) never launch duplicate probes.
  private discover_promise: Promise<void> | null = null;

  private initialized: boolean = false;
  private capturing: boolean = false;

  private worker: Worker | null = null;
  private worker_mode: boolean = false;

  private stream: MediaStream | null = null;
  private processor: any | null = null; // MediaStreamTrackProcessor
  private reader: ReadableStreamDefaultReader<VideoFrame> | null = null;

  private yuv_manager: YuvManager;
  has_logged_first_frame:boolean = false;
  private latest_capture_diagnostics = new Map<number, CaptureFrameDiagnostic>();
  private latest_track_settings: MediaTrackSettings | null = null;
  private logged_capture_context_for_run = false;

  // ---- Capture-path telemetry (logging only) ----------------------------
  // Per-second aggregate counters for the frame upload path (shared by the
  // worker and main-thread capture modes). Reset each interval by
  // log_capture_rates(). Never hold pixel data — only counts/dims/strides.
  private cap_frames_in: number = 0;      // frames entering upload_into_slot
  private cap_uploads: number = 0;        // successful writeTexture + publish
  private cap_no_slot_drops: number = 0;  // dropped: no Idle camera slot
  private cap_no_device_drops: number = 0;// dropped: GPU device not ready
  private cap_write_fail: number = 0;     // writeTexture/publish threw
  private cap_up_ms_sum: number = 0;      // sum of main-side upload latency
  private cap_up_samples: number = 0;
  private cap_last_seq: number = 0;       // latest published camera sequence
  private cap_rejected_frames: number = 0;// dropped: format resolve/validate failed

  // Deduplicated diagnostics: signature -> last-emit epoch ms. Bounds a noisy
  // per-frame failure (e.g. every frame rejected on a device) to one line per
  // window instead of flooding the console. Bounded in size to avoid growth.
  private diag_last_emit: Map<string, number> = new Map();
  private static readonly DIAG_WINDOW_MS = 5000;
  private static readonly DIAG_MAX_KEYS = 64;
  private static readonly CAPTURE_DIAG_MAX_KEYS = 24;

  constructor() {
    this.camera_pool = new ColorCameraPool();
    this.camera_info = new CameraInfo();
    this.yuv_manager = new YuvManager();
  }

  // ---- Lifecycle ----------------------------------------------------------

  /**
   * Idempotent. Detects platform, enumerates cameras, and picks a default.
   * Does NOT request camera permission for capture itself.
   */
  async init(): Promise<void> {
    if (this.initialized) return;

    this.worker_mode = this.detect_ios();

    await this.enumerate_cameras();
    this.select_default_camera();

    this.initialized = true;
  }

  /**
   * Tear down completely. Stops capture, releases pool textures.
   */
  shutdown(): void {
    this.stop();
    this.camera_pool.shutdown();
    this.initialized = false;
  }

  is_initialized(): boolean { return this.initialized; }
  is_capturing(): boolean { return this.capturing; }

  get_capture_diagnostic_for_sequence(sequence: number): CaptureFrameDiagnostic | null {
    return this.latest_capture_diagnostics.get(sequence) ?? null;
  }

  // ---- Camera-selector port ----------------------------------------------
  // Narrow, React-free surface the Stage-owned CameraSelectorService drives.
  // Keeps UI decoupled from CaptureService internals.

  /**
   * Idempotent, de-duped discovery. Runs the existing init() (enumerate +
   * probe) at most once; concurrent callers share the same in-flight promise so
   * no duplicate camera probes are launched. Safe to call before Scan.
   */
  async ensure_discovered(): Promise<void> {
    if (this.initialized) return;
    if (this.discover_promise) {
      await this.discover_promise;
      return;
    }
    this.discover_promise = this.init();
    try {
      await this.discover_promise;
    } finally {
      this.discover_promise = null;
    }
  }

  /** Enumerated cameras (empty until discovery has run). */
  get_cameras(): CameraInfo[] {
    return this.cameralist;
  }

  /**
   * Apply an explicit user override by exact deviceId. Re-runs selection so the
   * override is honored before the natural algorithm. Returns true if the
   * override was applied, false if that device is not present (in which case
   * selection falls back safely to Automatic).
   */
  set_manual_override(device_id: string): boolean {
    this.explicit_override_id = device_id;
    this.select_default_camera();
    return this.selected?.device_id === device_id;
  }

  /** Drop any explicit override and revert to the natural selection. */
  clear_manual_override(): void {
    this.explicit_override_id = null;
    this.select_default_camera();
  }

  /**
   * Build a complete, human-readable diagnostic dump for one enumerated camera
   * from data already captured during discovery — the MediaDeviceInfo, its
   * groupId, the raw probe track getSettings/getCapabilities/getConstraints
   * snapshots, and the CameraInfo/diagnostics. Opens NO new stream and touches
   * NO focus parameter. Returns delimited console lines for the in-app console
   * only; never throws (any failure is returned as a single error line).
   */
  get_selection_dump(device_id: string): string[] {
    try {
      const idx = this.cameralist.findIndex((c) => c.device_id === device_id);
      const info = idx >= 0 ? this.cameralist[idx] : null;
      const friendly = friendly_name_for(this.cameralist, device_id);
      const raw = this.raw_by_device_id.get(device_id) ?? null;

      return build_camera_dump({
        friendly,
        index: idx,
        facing: info?.facing ?? "unknown",
        selection_source: "manual override",
        device_id,
        group_id: raw?.group_id ?? null,
        media_device_info: raw?.media_device_info ?? null,
        raw_settings: raw?.settings ?? null,
        raw_capabilities: raw?.capabilities ?? null,
        raw_constraints: raw?.constraints ?? null,
        camera_info: info,
        diagnostics: info?.diagnostics ?? null,
        device_model: this.device_model,
        device_model_note: this.device_model_note,
      });
    } catch (err) {
      return [`[camera dump error] ${this.err_str(err)}`];
    }
  }

  // ---- Enumeration -------------------------------------------------------

  /**
   * Builds cameralist from navigator.mediaDevices.enumerateDevices. Each
   * device becomes a CameraInfo with whatever fields we can infer
   * pre-permission. Label may be empty until the user grants access at
   * least once on this origin.
   */
  
private async enumerate_cameras(): Promise<void> {
  this.cameralist = [];
  this.raw_by_device_id.clear();

  // Device-model diagnostics up front (startup/discovery). Never throws.
  await this.log_device_info();

  if (!navigator?.mediaDevices?.enumerateDevices) {
    console.warn("CaptureService.enumerate_cameras: mediaDevices not available");
    this.dbg("[camera] enumerate_cameras: mediaDevices.enumerateDevices unavailable");
    this.enumerate_state = "unavailable";
    return;
  }

  this.dbg("[camera] enumerate_cameras: begin");

  // Step 0: enumerate BEFORE requesting permission. On mobile the labels are
  // typically empty here (they only populate once a track is live), but this
  // records how many video inputs exist and their ids/groups up front. We
  // re-enumerate and re-log after permission below, where labels are present.
  try {
    const pre = await navigator.mediaDevices.enumerateDevices();
    this.log_devices("pre-permission", pre);
  } catch (err) {
    console.warn("CaptureService.enumerate_cameras: pre-permission enumerate failed", err);
    this.dbg(`[camera] pre-permission enumerate failed: ${this.err_str(err)}`);
  }

  // Step 1: open a generic stream to trigger / verify camera permission.
  // We don't care which camera answers — we just need an active video
  // track so subsequent enumerateDevices() returns real labels.
  let permission_stream: MediaStream | null = null;
  try {
    permission_stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: "environment" }, // hint, not required
    });
    this.dbg("[camera] permission granted (environment-hint stream opened)");
  } catch (err) {
    console.warn("CaptureService.enumerate_cameras: permission denied", err);
    this.dbg(`[camera] getUserMedia permission denied: ${this.err_str(err)}`);
    this.enumerate_state = "no-permission";
    return;
  }

  try {
    // Step 2: enumerate while the stream is still active. Labels are
    // populated only when at least one video track is live.
    let devices: MediaDeviceInfo[] = [];
    try {
      devices = await navigator.mediaDevices.enumerateDevices();
    } catch (err) {
      console.error("CaptureService.enumerate_cameras: enumerate failed", err);
      this.dbg(`[camera] post-permission enumerate failed: ${this.err_str(err)}`);
      this.enumerate_state = "error";
      return;
    }

    this.log_devices("post-permission", devices);

    let index = 0;
    for (const d of devices) {
      if (d.kind !== "videoinput") continue;

      const info = new CameraInfo();
      info.device_id = d.deviceId;
      info.label = d.label || "";
      info.facing = this.guess_facing(d.label);

      // Probe each specific camera. Stream is closed after each probe.
      await this.probe_capabilities(info);

      // Retain the raw MediaDeviceInfo reference + groupId alongside the raw
      // track snapshots probe_capabilities already stored, so an explicit
      // selection can dump everything the browser exposed without reopening a
      // stream. No accessors are invoked here.
      const raw = this.raw_by_device_id.get(info.device_id) ?? {
        media_device_info: null,
        group_id: null,
        settings: null,
        capabilities: null,
        constraints: null,
      };
      raw.media_device_info = d;
      raw.group_id = d.groupId ?? null;
      this.raw_by_device_id.set(info.device_id, raw);

      // One structured line per video input covering the fields needed to
      // diagnose camera selection (index/label/id/group/facing + the settings
      // the browser actually gave us).
      this.dbg(
        `[camera] videoinput[${index}] label="${info.label || "(empty)"}" ` +
          `facing=${info.facing} ` +
          `deviceId=${this.short_id(info.device_id)} ` +
          `groupId=${this.short_id(d.groupId)} ` +
          `settings=${info.width}x${info.height}@${Math.round(info.framerate)}fps ` +
          `format=${info.format}`
      );

      // Full per-camera WebRTC settings/capabilities + wide-angle heuristic.
      this.log_camera_diagnostics(index, info);

      this.cameralist.push(info);
      index++;
    }

    this.enumerate_state = "ok";
    this.dbg(`[camera] enumerate_cameras: ${this.cameralist.length} video input(s) found`);
    // Global caveat: the platform cannot confirm a physical lens type.
    this.dbg(`[camera] note: ${WEBRTC_LENS_NOTE}`);
  } finally {
    // Step 3: close the permission stream now that enumeration is done.
    for (const t of permission_stream.getTracks()) t.stop();
  }
}

  /**
   * Pick a default camera. Preference order: back-facing > unknown > front.
   * Mirrors selected fields into this.camera_info so start() has a single
   * active reference.
   */
  private select_default_camera(): void {
    if (this.cameralist.length === 0) {
      this.selected = null;
      this.last_selection_source = "Automatic";
      this.last_selection_reason = "no cameras available";
      this.dbg("[camera] select_default_camera: no cameras available, nothing selected");
      return;
    }

    // Explicit user override takes precedence over the natural algorithm: an
    // exact deviceId match wins here. On a miss we log and fall through to the
    // UNCHANGED automatic selection below (so Automatic behaves exactly as
    // before). This block is skipped entirely when no override is set.
    if (this.explicit_override_id) {
      const override = this.cameralist.find(
        (c) => c.device_id === this.explicit_override_id
      );
      if (override) {
        this.selected = override;
        this.copy_camera_info(this.selected, this.camera_info);
        const idx = this.cameralist.indexOf(override);
        const friendly = friendly_name_for(this.cameralist, override.device_id);
        const reason = `manual override: user explicitly selected ${friendly}`;
        this.last_selection_source = "manual override";
        this.last_selection_reason = reason;
        this.dbg(
          `[camera] selection source=manual override friendly="${friendly}" ` +
            `label="${override.label || "(empty)"}" facing=${override.facing} ` +
            `deviceId=${this.short_id(override.device_id)}`
        );
        this.dbg(
          `[camera] SELECTED videoinput[${idx}] label="${override.label || "(empty)"}" ` +
            `facing=${override.facing} ` +
            `deviceId=${this.short_id(override.device_id)} ` +
            `settings=${override.width}x${override.height}@${Math.round(
              override.framerate
            )}fps`
        );
        this.dbg(`[camera] selection reason: ${reason}`);
        // Manual override is authoritative even with empty probed settings; we
        // only WARN so the operator knows the diagnostic comparison may be
        // sparse. The override is still honored (no fallback here).
        if (!has_meaningful_settings(override.diagnostics?.settings ?? null)) {
          this.dbg(
            `[camera] WARNING: explicitly selected camera friendly="${friendly}" ` +
              `has empty probed settings; honoring manual override anyway`
          );
        }
        this.log_selected_summary(idx, override, reason);
        return;
      }
      this.dbg(
        `[camera] manual override deviceId=${this.short_id(this.explicit_override_id)} ` +
          `unavailable among ${this.cameralist.length} enumerated camera(s); ` +
          `falling back to automatic selection`
      );
    }

    this.dbg("[camera] selection source=automatic");

    // Back-facing candidates in existing deterministic (enumeration) order.
    // Automatic avoids a back camera that probed to EMPTY settings when another
    // usable back camera exists; a lone back (or all-empty backs) is preserved
    // unchanged. Manual override is unaffected — this filter is Automatic-only.
    const back_candidates = this.cameralist.filter((c) => c.facing === "back");
    const back_items = back_candidates.map((c) => ({
      camera: c,
      settings: c.diagnostics?.settings ?? null,
    }));
    const back_filter = filter_back_candidates(back_items);
    this.log_back_eligibility(back_candidates, back_filter);

    const back =
      back_filter.eligible.length > 0 ? back_filter.eligible[0].camera : undefined;
    const unknown = this.cameralist.find((c) => c.facing === "unknown");
    const front = this.cameralist.find((c) => c.facing === "front");

    // Preference order: back-facing > unknown > front > first enumerated.
    let reason: string;
    if (back) {
      this.selected = back;
      reason = this.back_selection_reason(back_candidates, back_filter);
    } else if (unknown) {
      this.selected = unknown;
      reason =
        "no camera reported a facing direction; picked the first camera of unknown facing";
    } else if (front) {
      this.selected = front;
      reason = "no back/unknown camera found; fell back to a front-facing camera";
    } else {
      this.selected = this.cameralist[0];
      reason = "no facing heuristic matched; fell back to the first enumerated camera";
    }

    this.last_selection_source = "Automatic";
    this.last_selection_reason = reason;

    this.copy_camera_info(this.selected, this.camera_info);

    const idx = this.cameralist.indexOf(this.selected);
    this.dbg(
      `[camera] SELECTED videoinput[${idx}] label="${this.selected.label || "(empty)"}" ` +
        `facing=${this.selected.facing} ` +
        `deviceId=${this.short_id(this.selected.device_id)} ` +
        `settings=${this.selected.width}x${this.selected.height}@${Math.round(
          this.selected.framerate
        )}fps`
    );
    this.dbg(`[camera] selection reason: ${reason}`);
    // Diagnostic aid: the selection algorithm keys only off the label facing
    // heuristic, so a device that labels a super-wide lens as "back" will win
    // the back-facing preference. Surface every back-facing candidate so an
    // unexpected wide-angle pick is visible here.
    const backs = this.cameralist.filter((c) => c.facing === "back");
    if (backs.length > 1) {
      this.dbg(
        `[camera] note: ${backs.length} back-facing candidates — first in enumeration order was chosen: ` +
          backs
            .map((c) => `"${c.label || "(empty)"}"@${c.width}x${c.height}`)
            .join(", ")
      );
    }

    this.log_selected_summary(idx, this.selected, reason);
  }

  // Concise diagnostic block for the finally-selected camera, drawn from the
  // settings/capabilities already captured during probe. Diagnostics only.
  private log_selected_summary(index: number, info: CameraInfo, branch_reason: string): void {
    const d = info.diagnostics;
    const settings = (d?.settings ?? {}) as Record<string, unknown>;
    const aspect = typeof settings.aspectRatio === "number" ? settings.aspectRatio : null;
    const resize = typeof settings.resizeMode === "string" ? settings.resizeMode : null;
    const zoom = extract_zoom(d?.capabilities ?? null);

    const lines = selected_summary({
      index,
      label: info.label,
      device_id: info.device_id,
      group_id: undefined,
      facing: info.facing,
      width: info.width,
      height: info.height,
      frame_rate: info.framerate,
      aspect,
      resize_mode: resize,
      zoom,
      wide: d?.wide ?? { is_hint: false, reasons: ["no diagnostics captured"] },
      branch_reason,
    });
    for (const line of lines) this.dbg(`[camera] ${line}`);
  }

  // Diagnostic listing of every back-facing candidate and whether its probed
  // settings snapshot was meaningful or empty, which candidates the Automatic
  // empty-settings filter excluded, and why the retained candidate was kept.
  // Diagnostics only; never affects manual override.
  private log_back_eligibility(
    backs: CameraInfo[],
    filter: BackFilterResult<{ camera: CameraInfo; settings: unknown }>
  ): void {
    if (backs.length === 0) return;

    this.dbg(
      `[camera] back-candidate settings eligibility (${backs.length} candidate(s)):`
    );
    for (const c of backs) {
      const meaningful = has_meaningful_settings(c.diagnostics?.settings ?? null);
      const excluded = filter.filtered && filter.excluded.some((e) => e.camera === c);
      this.dbg(
        `[camera]   - "${c.label || "(empty)"}" ` +
          `deviceId=${this.short_id(c.device_id)} ` +
          `${meaningful ? "meaningful settings" : "empty settings"}` +
          `${excluded ? " → EXCLUDED (empty settings; a meaningful back alternative exists)" : ""}`
      );
    }

    let retention: string;
    if (backs.length === 1) {
      retention = "only back candidate — retained regardless of settings";
    } else if (filter.filtered) {
      retention =
        `retained the first back candidate with meaningful settings; ` +
        `${filter.excluded.length} empty-settings candidate(s) excluded`;
    } else if (filter.all_empty) {
      retention =
        "all back candidates had empty settings — preserved existing behavior (none excluded)";
    } else {
      retention =
        "all back candidates have meaningful settings — normal eligible candidate";
    }
    this.dbg(`[camera]   retention: ${retention}`);
  }

  // Plain-language reason for the chosen back camera, mentioning the
  // empty-settings filter when it actually affected the selection.
  private back_selection_reason(
    backs: CameraInfo[],
    filter: BackFilterResult<{ camera: CameraInfo; settings: unknown }>
  ): string {
    if (backs.length === 1) {
      return has_meaningful_settings(backs[0].diagnostics?.settings ?? null)
        ? 'back-facing preference: label heuristic matched "back"/"rear"/"environment"'
        : "back-facing preference: the only back camera was retained despite empty " +
            "probed settings (no alternative back camera exists)";
    }
    if (filter.filtered) {
      return (
        "back-facing preference: chose the first back camera with meaningful probed " +
        `settings; ${filter.excluded.length} empty-settings back camera(s) excluded`
      );
    }
    if (filter.all_empty) {
      return (
        "back-facing preference: all back cameras had empty probed settings; " +
        "preserved the existing back-camera order"
      );
    }
    return 'back-facing preference: label heuristic matched "back"/"rear"/"environment"';
  }

  /**
   * UI hook to switch active camera. Caller should stop() and start()
   * again if currently capturing.
   */
  set_selected(info: CameraInfo): void {
    this.selected = info;
    this.copy_camera_info(info, this.camera_info);
  }

  private copy_camera_info(src: CameraInfo, dst: CameraInfo): void {
    dst.device_id = src.device_id;
    dst.label = src.label;
    dst.facing = src.facing;
    dst.format = src.format;
    dst.width = src.width;
    dst.height = src.height;
    dst.y_width = src.y_width;
    dst.y_height = src.y_height;
    dst.chroma_width = src.chroma_width;
    dst.chroma_height = src.chroma_height;
    dst.framerate = src.framerate;
    dst.probe = src.probe;
  }

  private guess_facing(label: string): CameraFacing {
    const l = label.toLowerCase();
    if (l.includes("back") || l.includes("rear") || l.includes("environment")) return "back";
    if (l.includes("front") || l.includes("user") || l.includes("face")) return "front";
    return "unknown";
  }

  /**
   * Optional capability probe. Opens a short-lived stream just to read
   * width/height/framerate, then closes it. May prompt for permission; on
   * permission denial we silently leave fields at zero and continue.
   *
   * Comment this call out in enumerate_cameras if you want to defer
   * probing until the user explicitly starts capture.
   */
  
 is_android(): boolean {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent ?? "";
  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? "";

  return /Android/i.test(ua) || /Android/i.test(platform);
}

// LAST-RESORT fallback only. This platform guess is used solely by
// resolve_yuv_format when a frame reports NO format AND its copyTo layout is
// ambiguous. It is NOT the normal-path format determinant — the per-frame
// VideoFrame.format / layout always wins.
preferred_yuv_format(): "I420" | "NV12" {
  return this.is_android() ? "I420" : "NV12";
}

  private async probe_capabilities(info: CameraInfo): Promise<void> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        deviceId: { exact: info.device_id },
        width:  { ideal: MAX_VIDEO_WIDTH,  max: PREFERRED_VIDEO_WIDTH },
        height: { ideal: MAX_VIDEO_HEIGHT, max: PREFERRED_VIDEO_HEIGHT },
        aspectRatio: 16 / 9,
        frameRate: { ideal: 30, max: 30 },
      },
    });
    const track = stream.getVideoTracks()[0];
    const settings = track.getSettings();
    info.width = settings.width ?? 0;
    info.height = settings.height ?? 0;
    info.framerate = settings.frameRate ?? 0;
    // NOTE: the YUV format is NOT decided here. It is resolved per-frame from
    // each VideoFrame's reported format/layout in upload_into_slot; the
    // platform preference is only a last-resort fallback for null formats.
    // camera_info.format keeps its default until the first frame sets it.

    // Capture the full WebRTC settings/capabilities/constraints exposed for
    // this track (never focus — the diagnostics module whitelists only safe
    // imaging fields). getCapabilities/getConstraints are optional in some
    // browsers, so read them defensively. This reuses the stream we already
    // opened for the resolution probe — no extra getUserMedia.
    const capabilities =
      typeof track.getCapabilities === "function"
        ? (track.getCapabilities() as Record<string, unknown>)
        : null;
    const constraints =
      typeof track.getConstraints === "function"
        ? (track.getConstraints() as Record<string, unknown>)
        : null;
    info.diagnostics = build_diagnostics(
      info.label,
      settings as Record<string, unknown>,
      capabilities,
      constraints
    );

    // Retain the RAW (un-whitelisted) snapshots for the diagnostic dump. These
    // are the exact objects the browser returned on the probe track; the
    // enumerate loop augments this entry with the MediaDeviceInfo + groupId.
    const raw = this.raw_by_device_id.get(info.device_id) ?? {
      media_device_info: null,
      group_id: null,
      settings: null,
      capabilities: null,
      constraints: null,
    };
    raw.settings = settings;
    raw.capabilities = capabilities;
    raw.constraints = constraints;
    this.raw_by_device_id.set(info.device_id, raw);

    // Resulting track settings/capabilities (never focus). facingMode is the
    // browser's own report and is logged next to our label-based guess so the
    // two can be compared during diagnosis.
    this.dbg(
      `[camera] probe "${info.label || "(empty)"}": ` +
        `${info.width}x${info.height}@${Math.round(info.framerate)}fps ` +
        `facingMode=${settings.facingMode ?? "(none)"} ` +
        `aspect=${settings.aspectRatio ?? "?"}`
    );
    track.stop();
    stream.getTracks().forEach((t) => t.stop());
  } catch (err) {
    console.warn(`CaptureService.probe_capabilities failed for ${info.label}`, err);
    this.dbg(`[camera] probe failed for "${info.label || "(empty)"}": ${this.err_str(err)}`);
  }
}

  // ---- Debug console helpers ---------------------------------------------
  // These forward camera diagnostics to the Stage-owned in-app ConsoleService
  // (for on-phone debugging) without changing any existing console.* behavior
  // and without touching focus. Guarded so a missing Stage/service can never
  // break enumeration.
  private dbg(message: string): void {
    try {
      Stage.instance().console_service.log(message);
    } catch {
      // Console diagnostics are best-effort; never let them affect capture.
    }
  }

  private err_str(err: unknown): string {
    if (err instanceof Error) return `${err.name}: ${err.message}`;
    return String(err);
  }

  /**
   * Emit a capture diagnostic at most once per DIAG_WINDOW_MS per signature,
   * to both console.warn and the in-app ConsoleService. Used for per-frame
   * format-rejection / fallback messages so a persistently bad device logs a
   * single throttled line instead of flooding at frame rate. Returns true if
   * the message was actually emitted this call.
   */
  private diag_warn(signature: string, message: string): boolean {
    const now = Date.now();
    const last = this.diag_last_emit.get(signature);
    if (last != null && now - last < CaptureService.DIAG_WINDOW_MS) {
      return false;
    }
    // Bound the map: drop the oldest entry when full.
    if (
      !this.diag_last_emit.has(signature) &&
      this.diag_last_emit.size >= CaptureService.DIAG_MAX_KEYS
    ) {
      const oldest = this.diag_last_emit.keys().next().value;
      if (oldest !== undefined) this.diag_last_emit.delete(oldest);
    }
    this.diag_last_emit.set(signature, now);
    console.warn(message);
    this.dbg(message);
    return true;
  }

  // Compact "off/stride" summary of a copyTo layout for diagnostics; never
  // logs pixel bytes.
  private describe_layout(layout: ReadonlyArray<PlaneLayoutLike>): string {
    if (!layout || !layout.length) return "(no planes)";
    return layout
      .map((p, i) => `p${i}{off:${p?.offset},stride:${p?.stride}}`)
      .join(",");
  }

  private camera_suffix(): string {
    return format_device_id_suffix(this.camera_info.device_id);
  }

  // Device/group ids can be long opaque strings; keep them distinguishable in
  // the log without dumping the full value on every line.
  private short_id(id: string | undefined): string {
    if (!id) return "(none)";
    return id.length <= 12 ? id : `${id.slice(0, 8)}…${id.slice(-4)}`;
  }

  // Collect and log device-model diagnostics (UA, low/high-entropy UA Client
  // Hints, screen/DPR) once at discovery. Stores the derived model + honest
  // availability note for the camera selector. Best-effort; never throws.
  private async log_device_info(): Promise<void> {
    try {
      const nav =
        typeof navigator !== "undefined"
          ? (navigator as unknown as NavigatorLike)
          : null;
      const scr = typeof screen !== "undefined" ? screen : null;
      const dpr = typeof window !== "undefined" ? window.devicePixelRatio : null;

      const basic = collect_basic_device_info(nav, scr, dpr);
      const high = await collect_high_entropy(nav);
      const derived = derive_model(basic, high);

      this.device_model = derived.model;
      this.device_model_note = derived.note;

      for (const line of format_device_info_lines(basic, high, derived)) {
        this.dbg(`[device] ${line}`);
      }
    } catch (err) {
      this.dbg(`[device] device-info collection failed: ${this.err_str(err)}`);
    }
  }

  // Emit the full WebRTC settings/capabilities/constraints for one camera as
  // compact, one-field-per-line output (readable on narrow mobile screens),
  // plus the ultra-wide heuristic for back cameras. Diagnostics only.
  private log_camera_diagnostics(index: number, info: CameraInfo): void {
    const d = info.diagnostics;
    if (!d) return;

    this.dbg(`[camera] videoinput[${index}] settings:`);
    for (const line of format_settings(d.settings)) this.dbg(`[camera]     ${line}`);

    this.dbg(`[camera] videoinput[${index}] capabilities:`);
    for (const line of format_capabilities(d.capabilities)) this.dbg(`[camera]     ${line}`);

    const constraint_lines = format_constraints(d.constraints);
    if (constraint_lines.length > 0) {
      this.dbg(`[camera] videoinput[${index}] constraints:`);
      for (const line of constraint_lines) this.dbg(`[camera]     ${line}`);
    }

    // The wide-angle heuristic is only meaningful for back cameras (front
    // cameras are effectively never ultra-wide on these devices).
    if (info.facing === "back") {
      this.dbg(
        `[camera] videoinput[${index}] wide-angle hint: ` +
          `${d.wide.is_hint ? "LIKELY ultra-wide (heuristic)" : "not indicated (heuristic)"}`
      );
      for (const reason of d.wide.reasons) this.dbg(`[camera]       - ${reason}`);
    }
  }

  private log_devices(phase: string, devices: MediaDeviceInfo[]): void {
    const vids = devices.filter((d) => d.kind === "videoinput");
    this.dbg(`[camera] ${phase}: enumerateDevices returned ${vids.length} videoinput(s)`);
    vids.forEach((d, i) => {
      this.dbg(
        `[camera] ${phase} videoinput[${i}] label="${d.label || "(empty)"}" ` +
          `deviceId=${this.short_id(d.deviceId)} groupId=${this.short_id(d.groupId)}`
      );
    });
  }

  // ---- Capture lifecycle -------------------------------------------------

  /**
   * Begin capturing from the active camera (this.camera_info.device_id).
   * Must be called from within a user-gesture handler on iOS Safari.
   */
  async start(): Promise<void> {
    if (this.capturing) return;

    rb_debug_log("[capture] start() called, worker_mode=", this.worker_mode, "device_id=", this.camera_info.device_id);

    if (!this.camera_info.device_id) {
      this.dbg("[camera] start failed: no camera selected (device_id empty)");
      throw new Error("CaptureService.start: camera_info.device_id is empty");
    }

    // High-visibility scan-start line for the camera ACTUALLY being used. This
    // reads the resolved selection (camera_info.device_id) — which already
    // reflects any fallback from an unavailable override to Automatic — so it
    // always matches the real camera, not merely the requested override. The
    // deviceId suffix is a quick comparison aid; full ids stay in the detailed
    // camera logs/dumps.
    {
      const used_id = this.camera_info.device_id;
      const friendly = friendly_name_for(this.cameralist, used_id);
      this.dbg(
        `[scan camera] ${friendly_with_suffix(friendly, used_id)} ` +
          `(source: ${this.last_selection_source}) — reason: ${this.last_selection_reason}`
      );
    }

    const constraints: MediaStreamConstraints = {
      audio: false,
      video: {
        deviceId: { exact: this.camera_info.device_id },
        width:  { ideal: MAX_VIDEO_WIDTH,  max: PREFERRED_VIDEO_WIDTH },
        height: { ideal: MAX_VIDEO_HEIGHT, max: PREFERRED_VIDEO_HEIGHT },
        aspectRatio: 16 / 9,
      },
    };

    try {
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      this.dbg(`[camera] start getUserMedia failed: ${this.err_str(err)}`);
      throw err;
    }
    const track = this.stream.getVideoTracks()[0];

     rb_debug_log("[capture] got stream, track=", track?.label, "settings=", track?.getSettings());
     {
       const s = track?.getSettings?.() ?? {};
       this.latest_track_settings = s;
       this.logged_capture_context_for_run = false;
       this.dbg(
         `[camera] capture started on "${track?.label || "(empty)"}": ` +
           `${s.width ?? "?"}x${s.height ?? "?"}@${Math.round(s.frameRate ?? 0)}fps ` +
           `facingMode=${s.facingMode ?? "(none)"}`
       );
     }
     
  // CRITICAL: set this before kicking off the pump, since the pump's
  // while loop reads it.
   this.capturing = true;

    if (this.worker_mode) {
      await this.start_worker_capture(track);
    } else {
      await this.start_main_capture(track);
    }

    this.capturing = true;
      rb_debug_log("[capture] start() done, capturing=true");
  }

  /**
   * Stops capture, terminates worker if any. Pool textures remain
   * allocated so a subsequent start() can reuse them.
   */
  stop(): void {
    if (!this.capturing) return;

    if (this.reader) {
      try { this.reader.cancel(); } catch {}
      this.reader = null;
    }
    this.processor = null;

    if (this.stream) {
      for (const t of this.stream.getTracks()) t.stop();
      this.stream = null;
    }

    if (this.worker) {
      this.worker.postMessage({ type: "stop" });
      this.worker.terminate();
      this.worker = null;
    }

    this.camera_pool.clear();
    this.latest_capture_diagnostics.clear();
    this.latest_track_settings = null;
    this.logged_capture_context_for_run = false;

    this.capturing = false;
  }

  // ---- Main-thread capture path -------------------------------------------

  private async start_main_capture(track: MediaStreamTrack): Promise<void> {
    const MSTP = (window as any).MediaStreamTrackProcessor;
     rb_debug_log("[capture] MediaStreamTrackProcessor available?", !!MSTP);
    if (!MSTP) {
      throw new Error(
        "MediaStreamTrackProcessor not available. Use the worker path or upgrade the browser."
      );
    }
    this.processor = new MSTP({ track });
    this.reader = this.processor.readable.getReader();
     rb_debug_log("[capture] reader acquired, starting pump");

    // Pull frames as fast as they arrive.
    this.pump_main().catch((err) => {
      console.error("CaptureService.pump_main failed", err);
    });
  }

  private async pump_main(): Promise<void> {
     let frame_count = 0;
    while (this.capturing && this.reader) {
      const { value: frame, done } = await this.reader.read();
      if (done)
      {
         rb_debug_log("[capture] pump: reader done");
         break;
      }
      if (!frame)
        {
            rb_debug_log("[capture] pump: empty frame");
           continue;
       }
       if (frame_count < 3)
       {
          rb_debug_log("[capture] pump received frame", frame_count, frame.codedWidth, "x", frame.codedHeight);
          frame_count++;
       }

      await this.on_video_frame(frame);
    }
     rb_debug_log("[capture] pump exited");
  }

  // ---- Worker-thread capture path (iOS) ----------------------------------

  private async start_worker_capture(track: MediaStreamTrack): Promise<void> {
    this.worker = new Worker(
      new URL("./capture_worker.ts", import.meta.url),
      { type: "module" }
    );

    this.worker.onmessage = (e: MessageEvent) => {
      const msg = e.data;
      if (!msg) return;

      if (msg.type === "frame") {
        this.on_worker_frame(msg);
      } else if (msg.type === "error") {
        console.error("CaptureService worker error:", msg.error);
      }
    };

        // MediaStreamTrack is transferable in supporting browsers.
    this.worker.postMessage(
      { type: "start", track },
      [track as unknown as Transferable]
    );
  }

  private on_worker_frame(msg: {
    bytes: ArrayBuffer;
    layout: any;
    format?: string | null;
    allocation_size?: number | null;
    visible_rect?: { x?: number; y?: number; width?: number; height?: number } | null;
    color_space?: {
      primaries?: string | null;
      transfer?: string | null;
      matrix?: string | null;
      fullRange?: boolean | null;
    } | null;
    coded_width: number;
    coded_height: number;
    display_width: number;
    display_height: number;
  }): void {
    const bytes = new Uint8Array(msg.bytes);
    this.upload_into_slot(
      bytes,
      msg.layout,
      {
        coded_width: msg.coded_width,
        coded_height: msg.coded_height,
        display_width: msg.display_width,
        display_height: msg.display_height,
      },
      msg.format ?? null,
      {
        path: "worker",
        allocation_size: msg.allocation_size ?? null,
        visible_rect: extractCaptureFrameRect(msg.visible_rect ?? null),
        color_space: extractCaptureColorSpace(msg.color_space ?? null),
      }
    ).catch((err) => {
      console.error("CaptureService.on_worker_frame upload failed", err);
    });
  }

  // ---- Shared upload path -------------------------------------------------

  /**
   * Main-thread VideoFrame entry. Copies plane bytes via frame.copyTo,
   * then delegates to upload_into_slot. Always closes the frame.
   */
  private async on_video_frame(frame: VideoFrame): Promise<void> {
      //console.log("[capture] on_video_frame entered, alloc_size=", frame.allocationSize());
    try {
      const alloc_size = frame.allocationSize();
      if (alloc_size <= 1) {
        frame.close();
        return; 
      }

      // Read the frame's self-reported format BEFORE copyTo/close so the
      // upload path can resolve I420 vs NV12 from frame truth, not a platform
      // guess. May be null/undefined on some platforms — handled downstream.
      const reported_format = (frame as { format?: string | null }).format ?? null;
      const visible_rect = extractCaptureFrameRect(
        (frame as unknown as { visibleRect?: unknown }).visibleRect ?? null
      );
      const color_space = extractCaptureColorSpace(
        (frame as unknown as { colorSpace?: unknown }).colorSpace ?? null
      );

      const buffer = this.yuv_manager.ensure_staging_buffer(alloc_size);
      const layout = await frame.copyTo(buffer);

      await this.upload_into_slot(
        buffer,
        layout,
        {
          coded_width: frame.codedWidth,
          coded_height: frame.codedHeight,
          display_width: frame.displayWidth,
          display_height: frame.displayHeight,
        },
        reported_format,
        {
          path: "main",
          allocation_size: alloc_size,
          visible_rect,
          color_space,
        }
      );
    } catch (err) {
      console.error("CaptureService.on_video_frame failed", err);
    } finally {
      frame.close();
    }
  }

  /**
   * Lazily allocate (or reinitialize) the ColorCameraPool on first frame /
   * resolution change, then write Y / chroma bytes into an Idle slot and
   * publish.
   */
  private async upload_into_slot(
    bytes: Uint8Array,
    layout: any,
    dims: {
      coded_width: number;
      coded_height: number;
      display_width: number;
      display_height: number;
    },
    reported_format: string | null | undefined,
    frameMeta: {
      path: "main" | "worker";
      allocation_size: number | null;
      visible_rect: ReturnType<typeof extractCaptureFrameRect>;
      color_space: CaptureFrameColorSpace | null;
    }
  ): Promise<void> {
    const device = Stage.instance().shader_service.get_device();

    // Telemetry: every inbound frame (worker post or main pump) reaches here.
    this.cap_frames_in++;
    const t_upload_start = performance.now();

    if (!this.has_logged_first_frame) {
    this.has_logged_first_frame = true;
    rb_debug_log("[capture] upload_into_slot called", {
      bytes: bytes.byteLength,
      worker_mode: this.worker_mode,
      pool_initialized: this.camera_pool.is_initialized(),
      coded: `${dims.coded_width}x${dims.coded_height}`,
      display: `${dims.display_width}x${dims.display_height}`,
    });
  }
    if (!device) {
      this.cap_no_device_drops++;
      this.log_capture_rates(null);
      return;
    }

    // Resolve THIS frame's YUV format from its own reported format first,
    // then from an unambiguous copyTo layout, and only as an explicitly
    // flagged last resort from the platform preference. Frame truth wins over
    // any probed/cached camera_info.format.
    const resolved = resolve_yuv_format(
      reported_format,
      layout as ReadonlyArray<PlaneLayoutLike>,
      this.preferred_yuv_format()
    );

    if (!resolved.ok) {
      this.cap_rejected_frames++;
      this.diag_warn(
        `reject:${reported_format ?? "null"}:${layout?.length}`,
        `[capture][reject] ${this.camera_suffix()} — ${resolved.reason}; ` +
          `layout=[${this.describe_layout(layout)}] ` +
          `coded=${dims.coded_width}x${dims.coded_height}`
      );
      this.log_capture_rates(null);
      return;
    }

    if (resolved.source === "platform-fallback" && resolved.warning) {
      this.diag_warn(
        `fallback:${layout?.length}:${resolved.format}`,
        `[capture][format-fallback] ${this.camera_suffix()} — ${resolved.warning}; ` +
          `layout=[${this.describe_layout(layout)}]`
      );
    }

    // Parse plane layout into an upload descriptor using the RESOLVED format.
    // build_descriptor re-validates plane count / stride / bounds and throws
    // on any inconsistency (e.g. a wrong last-resort guess), which we surface
    // as a deduplicated reject rather than a silent drop.
    let desc: YuvUploadDescriptor;
    try {
      desc = this.yuv_manager.build_descriptor(
        resolved.format,
        layout,
        bytes.byteLength,
        dims.coded_width,
        dims.coded_height,
        dims.display_width,
        dims.display_height
      );
    } catch (err) {
      this.cap_rejected_frames++;
      this.diag_warn(
        `descriptor:${resolved.format}:${layout?.length}`,
        `[capture][reject] ${this.camera_suffix()} — descriptor build failed for ` +
          `resolved format ${resolved.format} (source=${resolved.source}): ${this.err_str(err)}; ` +
          `layout=[${this.describe_layout(layout)}] coded=${dims.coded_width}x${dims.coded_height}`
      );
      this.log_capture_rates(null);
      return;
    }

    // Pool init on the very first frame.
    if (!this.camera_pool.is_initialized()) {
      this.initialize_pool_from_descriptor(device, desc);
    }

    // Defensive: rebuild pool if a resolution change slips through.
    if (this.pool_dimensions_changed(desc)) {
      this.camera_pool.shutdown();
      this.initialize_pool_from_descriptor(device, desc);
    }

    // Acquire a slot and write the planes.
    const slot = this.camera_pool.acquire_idle_slot();
    if (!slot) {
      // No free slot — drop this frame. In normal triple-buffer operation
      // this is rare; only happens if a consumer is holding a lease across
      // multiple frames.
      this.cap_no_slot_drops++;
      this.log_capture_rates(desc);
      return;
    }
    slot.begin_record();

    try {
      if (desc.format === "NV12") {
        const y = desc.planes[0];
        const uv = desc.planes[1];

        device.queue.writeTexture(
          { texture: slot.y_texture! },
          this.yuv_manager.slice_plane(bytes, y.offset, y.byteLength),
          { offset: 0, bytesPerRow: y.stride, rowsPerImage: y.height },
          { width: y.width, height: y.height, depthOrArrayLayers: 1 }
        );

        device.queue.writeTexture(
          { texture: slot.cbcr_texture! },
          this.yuv_manager.slice_plane(bytes, uv.offset, uv.byteLength),
          { offset: 0, bytesPerRow: uv.stride, rowsPerImage: uv.height },
          { width: uv.width, height: uv.height, depthOrArrayLayers: 1 }
        );
      } else {
        // I420
        const y = desc.planes[0];
        const u = desc.planes[1];
        const v = desc.planes[2];

        device.queue.writeTexture(
          { texture: slot.y_texture! },
          this.yuv_manager.slice_plane(bytes, y.offset, y.byteLength),
          { offset: 0, bytesPerRow: y.stride, rowsPerImage: y.height },
          { width: y.width, height: y.height, depthOrArrayLayers: 1 }
        );
        device.queue.writeTexture(
          { texture: slot.u_texture! },
          this.yuv_manager.slice_plane(bytes, u.offset, u.byteLength),
          { offset: 0, bytesPerRow: u.stride, rowsPerImage: u.height },
          { width: u.width, height: u.height, depthOrArrayLayers: 1 }
        );
        device.queue.writeTexture(
          { texture: slot.v_texture! },
          this.yuv_manager.slice_plane(bytes, v.offset, v.byteLength),
          { offset: 0, bytesPerRow: v.stride, rowsPerImage: v.height },
          { width: v.width, height: v.height, depthOrArrayLayers: 1 }
        );
      }

      // Publish: sequence + timestamp assigned by the pool.
      this.camera_pool.publish_slot(slot, performance.now() / 1000);
      const published_sequence = this.camera_pool.get_latest_published_sequence();
      this.record_capture_diagnostic(
        published_sequence,
        desc,
        layout as ReadonlyArray<PlaneLayoutLike>,
        reported_format ?? null,
        resolved.source,
        resolved.warning ?? null,
        frameMeta
      );

      this.cap_uploads++;
      this.cap_up_ms_sum += performance.now() - t_upload_start;
      this.cap_up_samples++;
      this.cap_last_seq = published_sequence;
    } catch (err) {
      this.cap_write_fail++;
      console.error("CaptureService.upload_into_slot: write failed", err);
      this.camera_pool.cancel_slot(slot);
    }

    this.log_capture_rates(desc);
  }

  /**
   * Logging only: once/sec, emit capture-upload aggregate counters (shared by
   * both worker and main modes) then reset them. Includes plane strides/dims
   * from the latest descriptor to surface stale-frame reuse / layout changes.
   * Never logs pixel bytes.
   */
  private log_capture_rates(desc: YuvUploadDescriptor | null): void {
    if (!rb_throttle("capture_upload")) return;

    const up_ms_avg = this.cap_up_samples > 0
      ? Math.round((this.cap_up_ms_sum / this.cap_up_samples) * 100) / 100
      : 0;

    const planes = desc
      ? desc.planes.map((p) => ({ w: p.width, h: p.height, stride: p.stride, off: p.offset }))
      : [];

    rb_log("capture_upload", {
      mode: this.worker_mode ? "worker" : "main",
      format: desc ? desc.format : this.camera_info.format,
      frames_in: this.cap_frames_in,
      uploads: this.cap_uploads,
      no_slot_drops: this.cap_no_slot_drops,
      no_device_drops: this.cap_no_device_drops,
      rejected_frames: this.cap_rejected_frames,
      write_fail: this.cap_write_fail,
      up_ms_avg,
      latest_seq: this.cap_last_seq,
      cam_idle: this.camera_pool.get_idle_count(),
      planes,
    });

    this.cap_frames_in = 0;
    this.cap_uploads = 0;
    this.cap_no_slot_drops = 0;
    this.cap_no_device_drops = 0;
    this.cap_rejected_frames = 0;
    this.cap_write_fail = 0;
    this.cap_up_ms_sum = 0;
    this.cap_up_samples = 0;
  }

  // ---- Pool sizing helpers -----------------------------------------------

  private initialize_pool_from_descriptor(
    device: GPUDevice,
    desc: YuvUploadDescriptor
  ): void {
    const y_width = desc.planes[0].width;
    const y_height = desc.planes[0].height;

    let chroma_width = 0;
    let chroma_height = 0;
    if (desc.format === "NV12") {
      chroma_width = desc.planes[1].width;
      chroma_height = desc.planes[1].height;
    } else {
      chroma_width = desc.planes[1].width;
      chroma_height = desc.planes[1].height;
    }

    this.camera_pool.initialize(
      device,
      "camera",
      desc.format,
      y_width,
      y_height,
      chroma_width,
      chroma_height
    );

    this.camera_info.set_resolution(
      desc.format,
      desc.displayWidth,
      desc.displayHeight,
      y_width,
      y_height,
      chroma_width,
      chroma_height
    );
  }

  private pool_dimensions_changed(desc: YuvUploadDescriptor): boolean {
    return (
      this.camera_pool.get_y_width() !== desc.planes[0].width ||
      this.camera_pool.get_y_height() !== desc.planes[0].height ||
      this.camera_pool.get_format() !== desc.format
    );
  }

  // ---- Platform detection -------------------------------------------------

  private detect_ios(): boolean {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const is_ios_like =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1);
    return is_ios_like;
  }

  private record_capture_diagnostic(
    sourceSequence: number,
    desc: YuvUploadDescriptor,
    layout: ReadonlyArray<PlaneLayoutLike>,
    reportedFormat: string | null,
    formatSource: FormatSource,
    formatWarning: string | null,
    frameMeta: {
      path: "main" | "worker";
      allocation_size: number | null;
      visible_rect: ReturnType<typeof extractCaptureFrameRect>;
      color_space: CaptureFrameColorSpace | null;
    }
  ): void {
    const nav =
      typeof navigator !== "undefined"
        ? (navigator as Navigator & { userAgentData?: { platform?: string } })
        : null;
    const screenOrientation = typeof screen !== "undefined" ? screen.orientation : null;
    const selectedCameraFriendly = friendly_name_for(this.cameralist, this.camera_info.device_id);
    const track = this.latest_track_settings;
    const trackResizeMode =
      typeof (track as MediaTrackSettings & { resizeMode?: unknown } | null)?.resizeMode === "string"
        ? ((track as MediaTrackSettings & { resizeMode?: string }).resizeMode ?? null)
        : null;

    const layoutPlanes: CaptureFramePlane[] = desc.planes.map((plane, index) => ({
      index,
      offset: layout[index]?.offset ?? plane.offset,
      stride: layout[index]?.stride ?? plane.stride,
      width: plane.width,
      height: plane.height,
      byteLength: plane.byteLength,
    }));

    const texturePlanes: CaptureTexturePlane[] = [
      { label: "Y", width: desc.planes[0].width, height: desc.planes[0].height },
    ];
    if (desc.format === "NV12") {
      texturePlanes.push({
        label: "CbCr",
        width: desc.planes[1].width,
        height: desc.planes[1].height,
      });
    } else {
      texturePlanes.push({
        label: "U",
        width: desc.planes[1].width,
        height: desc.planes[1].height,
      });
      texturePlanes.push({
        label: "V",
        width: desc.planes[2].width,
        height: desc.planes[2].height,
      });
    }

    const snapshot: CaptureFrameDiagnostic = {
      sourceSequence,
      path: frameMeta.path,
      selectedCameraFriendly,
      selectedCameraLabel: this.camera_info.label,
      selectedCameraSuffix: this.camera_suffix(),
      selectedCameraFacing: this.camera_info.facing,
      trackLabel: this.stream?.getVideoTracks?.()[0]?.label ?? null,
      trackWidth: track?.width ?? null,
      trackHeight: track?.height ?? null,
      trackFrameRate: track?.frameRate ?? null,
      trackFacingMode:
        typeof track?.facingMode === "string" ? track.facingMode : null,
      trackResizeMode: trackResizeMode,
      trackAspectRatio:
        typeof track?.aspectRatio === "number" ? track.aspectRatio : null,
      userAgent: nav?.userAgent ?? null,
      platform: nav?.platform ?? null,
      uaDataPlatform: nav?.userAgentData?.platform ?? null,
      deviceModel: this.device_model,
      reportedFormat,
      resolvedFormat: desc.format,
      formatSource,
      formatWarning,
      allocationSize: frameMeta.allocation_size,
      codedWidth: desc.codedWidth,
      codedHeight: desc.codedHeight,
      displayWidth: desc.displayWidth,
      displayHeight: desc.displayHeight,
      visibleRect: frameMeta.visible_rect,
      colorSpace: frameMeta.color_space,
      screenOrientationType: screenOrientation?.type ?? null,
      screenOrientationAngle:
        typeof screenOrientation?.angle === "number" ? screenOrientation.angle : null,
      layoutPlanes,
      texturePlanes,
    };

    this.latest_capture_diagnostics.set(sourceSequence, snapshot);
    while (this.latest_capture_diagnostics.size > CaptureService.CAPTURE_DIAG_MAX_KEYS) {
      const oldest = this.latest_capture_diagnostics.keys().next().value;
      if (oldest == null) break;
      this.latest_capture_diagnostics.delete(oldest);
    }

    if (!this.logged_capture_context_for_run) {
      this.logged_capture_context_for_run = true;
      this.dbg(buildCaptureFrameDiagnosticLog(snapshot));
    }
  }
}
